// What the last check found in Autotask or OneBill that no Omni product covers.
// Autotask items someone created directly there: Add (Omni owns it from then on)
// or Ignore. OneBill products nothing bills on: Add to Omni with a plan, or mark
// Not quoted in Omni. Both kinds of dismissal can be put back.
import React, { useState } from "react";
import type { CatalogDismissal } from "@phoneware/lens-domain";
import { Icon } from "../../icons.js";
import { plural } from "../../format.js";
import { useCatalog } from "./context.js";
import { pct, planText, scoreClass, usablePlans, type AutotaskRow, type OneBillRow } from "./model.js";

const PERIOD_TEXT: Record<string, string> = { one_time: "One-time", monthly: "Monthly", quarterly: "Bills quarterly", semi_annual: "Bills semi-annually", yearly: "Bills yearly" };

export function NotInOmni({
  dismissals,
  adopt,
  dismiss,
  restore,
}: {
  dismissals: CatalogDismissal[];
  adopt: (row: AutotaskRow) => void;
  dismiss: (source: "autotask" | "onebill", ref: string, name: string) => void;
  restore: (items: CatalogDismissal[]) => void;
}): React.ReactElement {
  const { list, checked, link, add, busy, links, manages } = useCatalog();
  const [query, setQuery] = useState("");
  const [limit, setLimit] = useState(20);

  if (!checked) {
    return (
      <section className="card">
        <div className="empty" data-testid="notin-unchecked">
          Omni hasn{"\u2019"}t checked Autotask and OneBill yet. Check now to list what{"\u2019"}s in them and not in Omni.
        </div>
      </section>
    );
  }

  const fromAutotask = checked.notInOmni.autotask;
  const fromOneBill = checked.notInOmni.onebill;
  const ignored = dismissals.filter((d) => d.source === "autotask");
  const notQuoted = dismissals.filter((d) => d.source === "onebill");
  const q = query.trim().toLowerCase();
  const matching = q ? fromOneBill.filter((o) => `${o.name} ${o.productCode}`.toLowerCase().includes(q)) : fromOneBill;
  const page = matching.slice(0, limit);

  /** The best unreviewed Omni product this OneBill product might be, when the name match is strong and a plan bills the same way. */
  function maybe(row: OneBillRow): { productId: string; name: string; score: number; planId: string } | null {
    for (const cand of row.candidates) {
      const p = list.find((x) => x.id === cand.productId);
      if (!p || !p.active || p.billingLink.state !== "unreviewed" || cand.score < 0.5) continue;
      const plan = usablePlans(row).find((pl) => pl.billing === p.billing);
      if (plan) return { productId: p.id, name: p.name, score: cand.score, planId: plan.id };
    }
    return null;
  }

  return (
    <div className="stack">
      <section className="card" data-testid="notin-autotask">
        <div className="card-h">
          <h2>In Autotask, not in Omni</h2>
          <span className="note">{plural(fromAutotask.length, "item")}</span>
        </div>
        <p className="card-note lead">Someone created these directly in Autotask. Add one and Omni owns it from then on; Ignore keeps it out of Omni.</p>
        {fromAutotask.length === 0 ? (
          <div className="empty" data-testid="notin-autotask-empty">
            <Icon name="circle-check" size={15} /> Every active Autotask product and service is in Omni. Anything created directly in Autotask shows up here after the next check.
          </div>
        ) : (
          fromAutotask.map((a) => (
            <div key={`${a.kind}-${a.id}`} className="ni-row" data-testid={`notin-at-${a.kind}-${a.id}`}>
              <div className="ni-main">
                <b>{a.name}</b>
                <span className="row-m">
                  Autotask {a.kind === "product" ? "Product" : "Service"} {a.id}
                  {a.period ? ` \u00b7 ${PERIOD_TEXT[a.period] ?? a.period}` : ""}
                  {a.active ? "" : " \u00b7 Inactive"}
                </span>
              </div>
              <div className="ni-end">
                {manages ? (
                  <button className="btn small primary" type="button" disabled={busy} data-testid={`adopt-${a.kind}-${a.id}`} onClick={() => adopt(a)}>
                    <Icon name="plus" size={14} />
                    Add
                  </button>
                ) : null}
                {links ? (
                  <button className="btn small" type="button" disabled={busy} data-testid={`ignore-${a.kind}-${a.id}`} onClick={() => dismiss("autotask", `${a.kind}-${a.id}`, a.name)}>
                    Ignore
                  </button>
                ) : null}
              </div>
            </div>
          ))
        )}
        {ignored.length ? (
          <p className="card-note" data-testid="notin-ignored">
            {plural(ignored.length, "item")} ignored: {ignored.map((d) => d.name || d.ref).join(", ")}.{" "}
            {links ? (
              <button className="link" type="button" disabled={busy} data-testid="put-back-autotask" onClick={() => restore(ignored)}>
                Put back
              </button>
            ) : null}
          </p>
        ) : null}
      </section>

      <section className="card" data-testid="notin-onebill">
        <div className="card-h">
          <h2>In OneBill, not in Omni</h2>
          <span className="note">{plural(fromOneBill.length, "OneBill product")}</span>
        </div>
        <p className="card-note lead">
          No Omni product bills on these. Add one to Omni and Omni creates it in Autotask, or mark it Not quoted in Omni if dealers never sell it.
        </p>
        <div className="bar">
          <label className="search">
            <Icon name="search" size={15} />
            <span className="sr">Search</span>
            <input
              className="input slim"
              type="search"
              data-testid="ni-search"
              placeholder="Search OneBill by name or code"
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                setLimit(20);
              }}
            />
          </label>
        </div>
        {fromOneBill.length === 0 ? (
          <div className="empty" data-testid="notin-onebill-empty">
            Every OneBill product bills an Omni product or is marked Not quoted in Omni.
          </div>
        ) : page.length === 0 ? (
          <div className="empty">No OneBill product here matches {"\u201c"}{query.trim()}{"\u201d"}.</div>
        ) : (
          page.map((o) => {
            const plans = usablePlans(o);
            const first = plans[0];
            const guess = maybe(o);
            return (
              <div key={o.productCode} className="ni-row" data-testid={`notin-ob-${o.productCode}`}>
                <div className="ni-main">
                  <b>{o.name}</b>
                  <span className="row-m">
                    {o.productCode}
                    {first ? ` \u00b7 plan ${first.id} \u00b7 ${planText(first)}` : " \u00b7 no plan that bills monthly or once"}
                    {plans.length > 1 ? ` \u00b7 ${plans.length - 1} more ${plans.length === 2 ? "plan" : "plans"}` : ""}
                  </span>
                  {guess && links ? (
                    <div className="maybe">
                      <span className={scoreClass(guess.score)}>{pct(guess.score)}</span>
                      <span>
                        Maybe <b>{guess.name}</b> in Omni, which needs a link
                      </span>
                      <button
                        className="btn small"
                        type="button"
                        disabled={busy}
                        data-testid={`maybe-link-${o.productCode}`}
                        onClick={() => {
                          const p = list.find((x) => x.id === guess.productId);
                          if (p) void link(p, { state: "linked", onebill: { productCode: o.productCode, pricePlanId: guess.planId } }, `Linked ${p.name} to OneBill ${o.productCode}, plan ${guess.planId}.`);
                        }}
                      >
                        <Icon name="link" size={14} />
                        Link them
                      </button>
                    </div>
                  ) : null}
                </div>
                <div className="ni-end">
                  {manages ? (
                    <button
                      className="btn small"
                      type="button"
                      disabled={busy || !first}
                      title={first ? undefined : "No plan that bills monthly or once"}
                      data-testid={`add-from-ob-${o.productCode}`}
                      onClick={() => add(o)}
                    >
                      <Icon name="plus" size={14} />
                      Add to Omni
                    </button>
                  ) : null}
                  {links ? (
                    <button className="btn small" type="button" disabled={busy} data-testid={`dismiss-ob-${o.productCode}`} onClick={() => dismiss("onebill", o.productCode, o.name)}>
                      <Icon name="ban" size={14} />
                      Not quoted in Omni
                    </button>
                  ) : null}
                </div>
              </div>
            );
          })
        )}
        {matching.length > page.length ? (
          <p className="more">
            <button className="btn small" type="button" onClick={() => setLimit(limit + 20)}>
              Show 20 more of {matching.length.toLocaleString("en-US")}
            </button>
          </p>
        ) : null}
        {notQuoted.length ? (
          <p className="card-note" data-testid="notin-notquoted">
            {plural(notQuoted.length, "OneBill product")} marked Not quoted in Omni: {notQuoted.map((d) => d.name || d.ref).join(", ")}.{" "}
            {links ? (
              <button className="link" type="button" disabled={busy} data-testid="put-back-onebill" onClick={() => restore(notQuoted)}>
                Put them back
              </button>
            ) : null}
          </p>
        ) : null}
      </section>
    </div>
  );
}
